// Barra de abas internas do admin. Ativar = navegar para a localização salva da aba (restaura URL/filtros).
// Fechar pede confirmação quando a aba tem alterações não salvas. Fechar a última volta à home do papel.
import { X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAdminTabs } from "@/store/useAdminTabs";
import { STAFF_HOME_ROUTE } from "@/lib/staffWelcome";
import type { AdminTab } from "./tabModel";

export function TabBar() {
  const navigate = useNavigate();
  const tabs = useAdminTabs((s) => s.tabs);
  const activeKey = useAdminTabs((s) => s.activeKey);
  const dirty = useAdminTabs((s) => s.dirty);
  const performClose = useAdminTabs((s) => s.performClose);

  if (tabs.length === 0) return null;

  const activate = (tab: AdminTab) =>
    navigate({ pathname: tab.location.pathname, search: tab.location.search });

  const close = (event: React.MouseEvent, tab: AdminTab) => {
    event.stopPropagation();
    if (dirty[tab.key]) {
      const ok = window.confirm(`A aba "${tab.title}" tem alterações não salvas. Fechar mesmo assim?`);
      if (!ok) return;
    }
    const { nextLocation, wentHome } = performClose(tab.key);
    // Fechar a última aba volta à tela inicial (boas-vindas do Nexo), a home do ambiente de funcionários.
    if (wentHome) navigate(STAFF_HOME_ROUTE);
    else if (nextLocation) navigate({ pathname: nextLocation.pathname, search: nextLocation.search });
  };

  return (
    <div
      role="tablist"
      aria-label="Abas abertas"
      className="flex items-stretch gap-1 overflow-x-auto border-b border-border bg-surface-elevated px-2 pt-1"
    >
      {tabs.map((tab) => {
        const isActive = tab.key === activeKey;
        return (
          <div
            key={tab.key}
            role="tab"
            aria-label={`Aba ${tab.title}`}
            aria-selected={isActive}
            tabIndex={0}
            onClick={() => activate(tab)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                activate(tab);
              }
            }}
            className={cn(
              "group flex min-w-[9rem] max-w-[14rem] cursor-pointer select-none items-center gap-2 rounded-t-lg border-x border-t px-3 py-2 text-sm",
              // Aba ativa: MESMO realce do item ativo da sidebar — fundo verde-floresta preenchido
              // (`bg-primary`) com texto claro (`text-primary-foreground`). Não altera o realce da sidebar.
              isActive
                ? "border-primary bg-primary font-semibold text-primary-foreground"
                : "border-transparent text-muted-foreground hover:bg-secondary/60",
            )}
          >
            {dirty[tab.key] && (
              <span
                aria-label="alterações não salvas"
                title="Alterações não salvas"
                className="h-2 w-2 shrink-0 rounded-full bg-amber-400"
              />
            )}
            <span className="flex-1 truncate">{tab.title}</span>
            <button
              type="button"
              aria-label={`Fechar aba ${tab.title}`}
              onClick={(e) => close(e, tab)}
              className={cn(
                "shrink-0 rounded p-0.5 opacity-70 transition group-hover:opacity-100",
                // "X" legível em cada fundo: claro sobre o verde da aba ativa; muted na inativa.
                isActive
                  ? "text-primary-foreground hover:bg-primary-foreground/20"
                  : "text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
              )}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
